import { useCallback, useEffect, useState } from 'react';
import {
  applyStorePwaHeadTags,
  bindBeforeInstallPrompt,
  defaultStorePwaInstallHintKind,
  isAndroidDevice,
  isIosDevice,
  isStorePwaStandalone,
  peekStorePwaInstallPrompt,
  registerStorePwaServiceWorker,
  setStorePwaInstallPromptCache,
  subscribeStorePwaInstallPrompt,
} from '../pwa/storePwaRuntime.js';

/** @typedef {{ prompt: () => Promise<void>, userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }> }} BeforeInstallPromptEvent */
/** @typedef {'ios' | 'android' | 'desktop'} StorePwaInstallHintKind */

/**
 * @param {{
 *   slug: string,
 *   tenant?: { nome?: string | null, logo_url?: string | null, theme_json?: unknown } | null,
 * }} options
 */
export function useStorePwa({ slug, tenant }) {
  const [installEvent, setInstallEvent] = useState(
    /** @type {BeforeInstallPromptEvent | null} */ (() => peekStorePwaInstallPrompt()),
  );
  const [hintOpen, setHintOpen] = useState(false);
  const [hintKind, setHintKind] = useState(
    /** @type {StorePwaInstallHintKind} */ ('android'),
  );
  const [installed, setInstalled] = useState(() => isStorePwaStandalone());

  useEffect(() => {
    setInstalled(isStorePwaStandalone());
  }, [slug]);

  useEffect(() => {
    if (!slug) return undefined;
    const restoreTitle = applyStorePwaHeadTags(slug, tenant ?? undefined);
    return () => {
      restoreTitle?.();
    };
  }, [slug, tenant?.nome, tenant?.logo_url, tenant?.theme_json]);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    registerStorePwaServiceWorker(slug).then(() => {
      if (!cancelled) setInstalled(isStorePwaStandalone());
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!slug || typeof window === 'undefined') return undefined;

    return subscribeStorePwaInstallPrompt((event) => {
      setInstallEvent(event);
    });
  }, [slug]);

  useEffect(() => {
    if (!slug || typeof window === 'undefined') return undefined;

    /** @param {Event} ev */
    function onBip(ev) {
      if (!('prompt' in ev)) return;
      bindBeforeInstallPrompt(/** @type {BeforeInstallPromptEvent} */ (ev));
    }

    function onInstalled() {
      setInstallEvent(null);
      setStorePwaInstallPromptCache(null);
      setInstalled(true);
      setHintOpen(false);
    }

    function onDisplayMode() {
      setInstalled(isStorePwaStandalone());
    }

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener('change', onDisplayMode);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener('change', onDisplayMode);
    };
  }, [slug]);

  const canInstallNative = Boolean(installEvent) && !installed;
  const canShowInstall = Boolean(slug) && !installed;

  const install = useCallback(async () => {
    if (installed) return false;

    const nativeEvent = installEvent ?? peekStorePwaInstallPrompt();
    if (nativeEvent) {
      await nativeEvent.prompt();
      if (nativeEvent.userChoice) {
        await nativeEvent.userChoice;
      }
      setInstallEvent(null);
      setStorePwaInstallPromptCache(null);
      return true;
    }

    if (isIosDevice() || isAndroidDevice()) {
      setHintKind(defaultStorePwaInstallHintKind());
      setHintOpen(true);
      return true;
    }

    setHintKind('desktop');
    setHintOpen(true);
    return true;
  }, [installEvent, installed]);

  const dismissHint = useCallback(() => {
    setHintOpen(false);
  }, []);

  return {
    installed,
    canShowInstall,
    canInstallNative,
    hintOpen,
    hintKind,
    install,
    dismissHint,
  };
}
